import { toaster } from "components/common/Toast"
import { useState } from "react"
import { useDispatch } from "react-redux"

/**
 * Custom hook to manage modal states and handlers for profile details (crew/staff)
 * Handles booking conflict detection when assigning experiences
 *
 * @param {string|number} profileId - Profile ID (crew member or staff)
 * @param {string} profileName - Profile name for display in modals
 * @param {Function} updateProfileAction - Redux action to update profile
 * @param {Function} refreshAction - Optional Redux action to refresh data after update
 */
export const useProfileDetailsModals = (profileId, profileName, updateProfileAction, refreshAction) => {
  const dispatch = useDispatch()

  // Assign experiences conflict modal state
  const [assignConflictModalOpen, setAssignConflictModalOpen] = useState(false)
  const [assignConflictExperiences, setAssignConflictExperiences] = useState([])
  const [conflictResourceName, setConflictResourceName] = useState("")

  /**
   * Handle profile update with booking conflict detection
   * Wraps profile update action to intercept and handle booking conflicts
   *
   * @param {string|number} id - Profile ID
   * @param {Object|FormData} payload - Update payload (JSON or FormData)
   * @returns {Promise} - Resolves on success, rejects with error (including isBookingConflict flag)
   */
  const handleProfileUpdate = async (id, payload) => {
    try {
      const response =await dispatch(updateProfileAction(id, payload))
      // Refresh data after successful update if refresh action provided
      if (refreshAction) {
        await dispatch(refreshAction(id))
      }
      toaster.success(response?.message)
    } catch (error) {
      // Check for booking conflicts
      if (error?.data?.conflicting_bookings?.length > 0) {
        // Use conflicting_for from API or fall back to profileName
        const resourceName = error.data.conflicting_for || profileName

        // Group bookings by experience name
        const bookingsByExperience = {}

        error.data.conflicting_bookings.forEach((booking) => {
          const expName = booking.experience_name || "Unknown Experience"

          if (!bookingsByExperience[expName]) {
            bookingsByExperience[expName] = []
          }

          bookingsByExperience[expName].push({
            id: booking.booking_id,
            experienceName: expName,
            time: `${booking.start_time} - ${booking.end_time}`,
            date: booking.booked_date || "",
            imageUrl: booking.preview || "",
            location: booking.address || "",
            booking_uuid: booking.booking_uuid
          })
        })

        // Convert to array format for the modal
        const conflictingExperiences = Object.keys(bookingsByExperience).map((expName) => ({
          experienceName: expName,
          bookings: bookingsByExperience[expName]
        }))

        setConflictResourceName(resourceName)
        setAssignConflictExperiences(conflictingExperiences)
        setAssignConflictModalOpen(true)

        // Create error with custom flag for caller to detect
        const conflictError = new Error("Booking conflict")
        conflictError.isBookingConflict = true
        throw conflictError
      }
      throw error
    }
  }

  /**
   * Handle cancellation of assign conflict modal
   */
  const handleCancelAssignConflicts = () => {
    setAssignConflictModalOpen(false)
    setAssignConflictExperiences([])
    setConflictResourceName("")
  }

  // Props for RemoveExperienceWithBookingsModal (assign conflicts)
  const assignConflictModalProps = {
    open: assignConflictModalOpen,
    onCancel: handleCancelAssignConflicts,
    resourceName: conflictResourceName || profileName,
    conflictingExperiences: assignConflictExperiences,
    isGroupedView: true
  }

  return {
    handleProfileUpdate,
    handleCancelAssignConflicts,
    assignConflictModalProps
  }
}
